import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Wrench, ArrowLeftRight, Building2, DollarSign, ContactRound, Users, AlertCircle, Tag } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { IMPORT_TYPES, IMPORT_HEADERS } from "@/other/constants/OtherKeys";

interface ImportExportButtonProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

const ImportExportButton = ({ icon, title, description, onClick, disabled }: ImportExportButtonProps) => {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      className="h-auto py-4 px-6 flex items-center justify-start gap-4 hover:border-primary hover:bg-accent/50 transition-all duration-200 disabled:opacity-50 group"
    >
      <div className="w-10 h-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 dark:group-hover:bg-primary/30 transition-colors">
        <div className="text-primary">{icon}</div>
      </div>
      <div className="flex-1 text-left">
        <div className="font-semibold text-base mb-1 text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
    </Button>
  );
};

const BoardImportExportDetails = ({ subcategory }: SubcategoryDetailProps) => {
  const { user, editorHandler } = useSwivuBryntumContext();

  const canImport = user?.can(GRANTS.grant_three);
  const isDemoUser = user?.isDemoUser;
  const isDisabled = isDemoUser || !canImport;

  const handleImportResources = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.resources,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.resources,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportTasks = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.tasks,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.tasks,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportClients = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.clients,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.clients,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportBudget = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.taskBudget,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.taskBudget,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportContacts = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.contacts,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.contacts,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportBoardUsers = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.boardUsers,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.boardUsers,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  };

  const handleImportMarketingTags = () => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType: IMPORT_TYPES.marketingTags,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.marketingTags,
        labelIndex: 1
      },
      { useExisting: true, by: "recordType" }
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">{subcategory.description}</p>
      </div>

      <div className="space-y-5 max-w-5xl">
        {/* Info Card */}
        {isDisabled && (
          <Card className="py-3 px-4 border-primary/20 bg-primary/5">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <AlertCircle className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  {isDemoUser ? "Import/Export is not available for demo users." : "You need at least Contributor permissions to import or export data."}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Import/Export Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ImportExportButton icon={<Wrench className="h-5 w-5" />} title="Resources" description="Import or export resource data" onClick={handleImportResources} disabled={isDisabled} />

          <ImportExportButton icon={<ArrowLeftRight className="h-5 w-5" />} title="Tasks" description="Import or export task data" onClick={handleImportTasks} disabled={isDisabled} />

          <ImportExportButton icon={<Building2 className="h-5 w-5" />} title="Clients" description="Import or export client data" onClick={handleImportClients} disabled={isDisabled} />

          <ImportExportButton icon={<DollarSign className="h-5 w-5" />} title="Budget" description="Import or export budget data" onClick={handleImportBudget} disabled={isDisabled} />

          <ImportExportButton icon={<ContactRound className="h-5 w-5" />} title="Contacts" description="Import or export contact data" onClick={handleImportContacts} disabled={isDisabled} />

          <ImportExportButton icon={<Users className="h-5 w-5" />} title="Board Users" description="Import or export board user data" onClick={handleImportBoardUsers} disabled={isDisabled} />

          <ImportExportButton icon={<Tag className="h-5 w-5" />} title="Marketing Tags" description="Import marketing tags from CSV" onClick={handleImportMarketingTags} disabled={isDisabled} />
        </div>

        {/* Additional Info */}
        <Card className="py-3 px-4 border-primary/20 bg-primary/5">
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertCircle className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground">
                Each import/export option opens a dialog where you can upload files, download templates, or export existing data. Make sure your data follows the required format for successful
                imports.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BoardImportExportDetails;
